
import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingDown, DatabaseIcon } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import LiquidityCoverageChart from './charts/LiquidityCoverageChart';
import SLSChart from './charts/SLSChart';
import { defaultLiquidityData } from './data/forecastingData';

const BalanceSheetImpact = () => {
  const [scenario, setScenario] = useState("normal");
  const [timeframe, setTimeframe] = useState("monthly");
  const { toast } = useToast();

  const handleGenerateReport = () => {
    toast({
      title: "Report Generated",
      description: `Balance sheet impact report for ${scenario} scenario has been generated`,
    });
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
        <Card className="md:col-span-4">
          <CardHeader>
            <CardTitle className="flex items-center">
              <DatabaseIcon className="h-5 w-5 mr-2" />
              Balance Sheet Parameters
            </CardTitle>
            <CardDescription>
              Configure rundown scenarios for Basel III metrics
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="scenario-select">Rundown Scenario</Label>
              <Select value={scenario} onValueChange={setScenario}>
                <SelectTrigger id="scenario-select">
                  <SelectValue placeholder="Select scenario" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="normal">Normal Conditions</SelectItem>
                  <SelectItem value="moderate">Moderate Stress</SelectItem>
                  <SelectItem value="severe">Severe Stress</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="timeframe-select">Projection Timeframe</Label>
              <Select value={timeframe} onValueChange={setTimeframe}>
                <SelectTrigger id="timeframe-select">
                  <SelectValue placeholder="Select timeframe" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="monthly">Monthly</SelectItem>
                  <SelectItem value="quarterly">Quarterly</SelectItem>
                  <SelectItem value="annual">Annual</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="pt-4">
              <Button className="w-full" onClick={handleGenerateReport}>
                Generate Report
              </Button>
            </div>
          </CardContent>
        </Card>
        
        <Card className="md:col-span-8">
          <CardHeader>
            <CardTitle>Basel III Metrics Projection</CardTitle>
            <CardDescription>
              LCR and SLS projections based on balance sheet rundown
            </CardDescription>
          </CardHeader>
          <CardContent className="p-0">
            <Tabs defaultValue="lcr" className="w-full">
              <div className="border-b px-6">
                <TabsList className="h-12">
                  <TabsTrigger value="lcr">Liquidity Coverage Ratio</TabsTrigger>
                  <TabsTrigger value="sls">Structural Liquidity Statement</TabsTrigger>
                </TabsList>
              </div>
              <div className="p-6">
                <TabsContent value="lcr" className="mt-0 space-y-6">
                  <div className="h-[300px]">
                    <LiquidityCoverageChart />
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">LCR Breakdown</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Component</TableHead>
                          <TableHead className="text-right">Current</TableHead>
                          <TableHead className="text-right">30 Days</TableHead>
                          <TableHead className="text-right">90 Days</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        <TableRow>
                          <TableCell>HQLA (₹ Cr)</TableCell>
                          <TableCell className="text-right">1,250.00</TableCell>
                          <TableCell className="text-right">1,175.50</TableCell>
                          <TableCell className="text-right">1,095.25</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Net Cash Outflows (₹ Cr)</TableCell>
                          <TableCell className="text-right">1,050.00</TableCell>
                          <TableCell className="text-right">1,120.75</TableCell>
                          <TableCell className="text-right">1,186.50</TableCell>
                        </TableRow>
                        <TableRow className="font-medium">
                          <TableCell>LCR (%)</TableCell>
                          <TableCell className="text-right text-green-600">119.05%</TableCell>
                          <TableCell className="text-right text-amber-600">104.88%</TableCell>
                          <TableCell className="text-right text-red-600">92.31%</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell>Regulatory Requirement (%)</TableCell>
                          <TableCell className="text-right">100.00%</TableCell>
                          <TableCell className="text-right">100.00%</TableCell>
                          <TableCell className="text-right">100.00%</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
                
                <TabsContent value="sls" className="mt-0 space-y-6">
                  <div className="h-[300px]">
                    <SLSChart />
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium mb-2">SLS Maturity Buckets</h4>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Time Bucket</TableHead>
                          <TableHead className="text-right">Inflows (₹ Cr)</TableHead>
                          <TableHead className="text-right">Outflows (₹ Cr)</TableHead>
                          <TableHead className="text-right">Net (₹ Cr)</TableHead>
                          <TableHead className="text-right">Cumulative (₹ Cr)</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {defaultLiquidityData.map((item) => (
                          <TableRow key={item.bucket}>
                            <TableCell>{item.bucket}</TableCell>
                            <TableCell className="text-right">{item.inflows}</TableCell>
                            <TableCell className="text-right">{item.outflows}</TableCell>
                            <TableCell className={`text-right ${parseFloat(item.net) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {item.net}
                            </TableCell>
                            <TableCell className={`text-right font-medium ${parseFloat(item.cumulative) < 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {item.cumulative}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              </div>
            </Tabs>
          </CardContent>
        </Card>
      </div>
      
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Management Summary</CardTitle>
            <CardDescription>Key insights from balance sheet impact analysis</CardDescription>
          </div>
          <TrendingDown className={`h-6 w-6 ${scenario === 'severe' ? 'text-red-500' : scenario === 'moderate' ? 'text-amber-500' : 'text-green-500'}`} />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="bg-muted p-4 rounded-md">
              <h4 className="font-medium mb-1">LCR Impact</h4>
              <p className="text-sm">
                {scenario === 'severe' 
                  ? "Under severe stress conditions, LCR is projected to fall below regulatory threshold within 60 days. Recommend activating contingency funding plan and increasing HQLA holdings." 
                  : scenario === 'moderate' 
                    ? "Moderate stress shows LCR remaining compliant but trending downward. Consider increasing liquid asset holdings to provide additional buffer."
                    : "Under normal conditions, LCR remains well above regulatory requirements throughout the forecast period with sufficient buffer for unexpected events."}
              </p>
            </div>
            
            <div className="bg-muted p-4 rounded-md">
              <h4 className="font-medium mb-1">SLS Assessment</h4>
              <p className="text-sm">
                {scenario === 'severe' 
                  ? "Severe stress reveals significant structural liquidity gaps in the 3-6 month bucket. Consider extending liability duration and reducing concentration risk." 
                  : scenario === 'moderate' 
                    ? "Moderate stress indicates potential mismatches in the medium-term buckets. Recommended to diversify funding sources to improve resilience."
                    : "Balance sheet structure shows well-matched asset-liability profiles across all time buckets under normal conditions."}
              </p>
            </div>
            
            <div className="bg-muted p-4 rounded-md">
              <h4 className="font-medium mb-1">Risk Mitigation Recommendations</h4>
              <p className="text-sm">
                {scenario === 'severe' 
                  ? "1. Secure additional committed credit lines. 2. Reduce non-essential lending. 3. Increase retail deposit acquisition efforts." 
                  : scenario === 'moderate' 
                    ? "1. Realign investment portfolio towards more liquid securities. 2. Review large deposit concentration. 3. Adjust pricing to attract stable deposits."
                    : "1. Maintain current asset-liability management strategy. 2. Consider opportunities for optimizing returns on excess liquidity."}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BalanceSheetImpact;
